"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TempNameGenerator = void 0;
/**
 * Sometimes we need a series of temp names, normally for testing, and we need them generated in such a way
 *  that we can identify that they are in fact temp names at a later date. This is what this class gives us.
 *
 * In testing this will occur with frequency when we have to interact with some resource that is effectively
 *  a real life singleton. Think physical database server, snowflake account, GCP account. We can't just spin
 *  up a hundred to test against, or if we can spin up a hundred something needs to manage the naming of those
 *  hundred and tear them down again...
 */
const luxon_1 = require("luxon");
const slug_1 = require("./slug");
const ErrorWithMeta_1 = require("./ErrorWithMeta");
function dateTimeString(date) {
    return date.toFormat('yyMMddHHmm');
}
function parseDateTimeString(s) {
    if (s.length !== 10) {
        return null;
    }
    return luxon_1.DateTime.fromFormat(s, 'yyMMddHHmm');
}
/**
 * Creates a generator of strings that looks like this:
 *
 * tmp_<ts part>_<basis>_<counter>_<slug>
 *
 * e.g. `tmp_230206_example_1_hjdak`
 *
 * This has a length of 23 + the length of the basis
 *
 * This is useful for being able to identify and delete them later
 *
 * The "ts part" encodes an expiry timestamp, to the minute
 */
class TempNameGenerator {
    /**
     * Creates a temp name generator.
     * Basis should be a short descriptive string, ideally with no non-alphanumeric characters
     */
    constructor(basis, defaultAge = { days: 1 }) {
        this.generated = [];
        this.slug_part = (0, slug_1.slug)(5, 'qwertasdfgzxcvbyuiophjklnm');
        this.incrementor = ((i) => () => i++)(0);
        /**
         * Determines whether the given name follows the temp name format
         */
        this.isTemp = TempNameGenerator.isTemp;
        /**
         * Determines whether a name is older than a given age
         *
         * If the name isn't a temp name, we return false.
         * If the name isn't older than the given age, we return false.
         */
        this.isOld = TempNameGenerator.isOld;
        // Give some visibility into which file called it
        this.basis_part = basis
            .replace(/[^a-zA-Z]/g, '')
            .slice(-20)
            .toLowerCase();
        this.defaultAge = defaultAge;
        // Make these methods easier to use on lists
        this.get = this.get.bind(this);
        this.list = this.list.bind(this);
        this.isTemp = this.isTemp.bind(this);
        this.isCurrent = this.isCurrent.bind(this);
        this.isOld = this.isOld.bind(this);
        this.isSuitableForCleanUp = this.isSuitableForCleanUp.bind(this);
    }
    /**
     * Creates a new temp name
     */
    get(date = luxon_1.DateTime.now(), age = this.defaultAge) {
        const expires = date.plus(age);
        if (!expires.isValid) {
            throw new ErrorWithMeta_1.ErrorWithMeta(`Expiry is not valid`, {
                explanation: expires.invalidExplanation,
            });
        }
        const name = [
            'tmp',
            dateTimeString(expires),
            this.basis_part,
            this.incrementor(),
            this.slug_part, // 5
        ].join('_'); // 4
        this.generated.push(name);
        return name;
    }
    /**
     * Lists all of the names this generator has generated so far.
     */
    list() {
        return this.generated;
    }
    /**
     * Determines whether the given name follows the temp name format
     */
    static isTemp(name) {
        // Just test for starting with the temp and a date-like
        return /^tmp_[0-9]{10}_[a-zA-Z]+_\d+_[a-z]{5}$/.test(name);
    }
    /**
     * Determines whether the given name was generated by *this* generator.
     *
     * Does not determine whether it is old.
     */
    isCurrent(name) {
        return this.generated.includes(name);
    }
    /**
     * Determines whether a name is older than a given age
     *
     * If the name isn't a temp name, we return false.
     * If the name isn't older than the given age, we return false.
     */
    static isOld(name, benchmark = luxon_1.DateTime.now()) {
        if (!benchmark.isValid) {
            throw new ErrorWithMeta_1.ErrorWithMeta(`Benchmark DateTime is invalid`, {
                benchmark: benchmark.invalidExplanation,
            });
        }
        if (TempNameGenerator.isTemp(name)) {
            const [prefix, date, basis, count, slug] = name.split('_');
            if (prefix !== 'tmp') {
                return false;
            }
            if (!/^[a-zA-Z]+$/.test(basis)) {
                // Basis doesn't parse
                return false;
            }
            if (!/^\d+$/.test(count)) {
                // Count doesn't parse
                return false;
            }
            if (!/^[a-z]+$/.test(slug) || slug.length !== 5) {
                // Slug doesn't parse
                return false;
            }
            const dt = parseDateTimeString(date);
            if (dt === null || !dt.isValid) {
                // Date doesn't parse
                return false;
            }
            // Actually check if it is old
            return dt < benchmark;
        }
        return false;
    }
    /**
     * Determines whether we have jurisdiction to clean this name up
     *
     * This is when either:
     * * we own it
     * * it is old
     */
    isSuitableForCleanUp(name, benchmark) {
        return this.isCurrent(name) || this.isOld(name, benchmark);
    }
}
exports.TempNameGenerator = TempNameGenerator;
//# sourceMappingURL=tempName.js.map